import { computed, makeObservable, observable, runInAction } from 'mobx';

import { api } from '../../api';
import { Model } from '../../core/engine';
import { ManyToOne } from '../../core/engine/decorators';
import { getFullName } from '../../lib/utils/get-full-name';

import { Project, User } from './';

type ShareLinkSettings = {
  canDownload: boolean;
  canComment: boolean;
};

export class ShareLink extends Model {
  name: string;

  projectId: string;

  spaceId: string;

  url: string;

  /*
   * Initializing with empty object as settings on old links might
   * be undefined and it throws an exception when calling update method
   */
  settings: ShareLinkSettings = {} as ShareLinkSettings;

  recipients: string[];

  assetIds: string[] = [];

  expiresAt?: Date;

  /**
   * Whether viewers must enter a password. The password itself never reaches the client after it
   * is sent: the API stores a hash and only reports this flag.
   */
  hasPassword = false;

  creatorId: string;

  @ManyToOne()
  creator: User;

  @ManyToOne('shareLinks')
  public project: Project;

  constructor() {
    super('share-links');
    this.assetIds = [];

    makeObservable(this, {
      name: observable,
      project: observable,
      settings: observable,
      assetIds: observable,
      hasPassword: observable,
      creatorName: computed,
    });
  }

  get creatorName() {
    if (!this.creator) {
      return '';
    }

    return getFullName(this.creator);
  }

  async update(value: any) {
    await super.update(value);

    if (typeof value.canDownload === 'boolean') {
      this.settings.canDownload = value.canDownload;
    }
  }

  /**
   * Sets or replaces (string) or removes (null) the password.
   *
   * Deliberately not `update()`: that merges the payload into this entity, which would keep the
   * plaintext password in MobX state for as long as the link is in the pool.
   */
  async setPassword(password: string | null) {
    await api.patch(`/${this.$name}/${this._id}`, { password });

    runInAction(() => {
      this.hasPassword = password !== null;
    });
  }

  toPOJO(): Record<string, any> {
    return {
      _id: this._id,
      name: this.name,
    };
  }

  static override getOne(id: string): ShareLink | undefined {
    return Model.getOne(id) as ShareLink;
  }

  static override getAll(): ShareLink[] {
    return Model.getAll().filter((entity) => entity instanceof ShareLink) as ShareLink[];
  }

  static removeAsset(assetId: string) {
    const shareLinks = this.getAll();

    shareLinks.forEach((shareLink) => {
      shareLink.assetIds = shareLink.assetIds.filter((id) => id !== assetId);

      // remove the share link if no assetIds left
      if (shareLink.assetIds.length === 0) {
        shareLink.remove();
      }
    });
  }
}
